import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Card, Col, Container, FormControl, Row } from 'react-bootstrap';
import { ChevronDown, ChevronRight, Clipboard } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { copyToClipboard } from '../../helpers/global';
import Loader from '../Loader';
import Tabs from '../common/Tabs';

// Strip {{ }}, turn | into spaces, collapse whitespace so partial refs still match
const normalizeForSearch = value =>
  String(value || '')
    .toLowerCase()
    .replace(/\{\{|\}\}/g, ' ')
    .replace(/\|/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const matchesQuery = (variable, rawQuery) => {
  const query = normalizeForSearch(rawQuery);
  if (!query) return true;

  const haystack = normalizeForSearch(`${variable.label} ${variable.reference}`);
  // Every token must appear (order-independent) so `{{ parent| }}` and `parent child` both work
  return query.split(' ').every(token => haystack.includes(token));
};

const DocHubVariables = () => {
  const [loading, setLoading] = useState(true);
  const [models, setModels] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [query, setQuery] = useState('');
  const [openKey, setOpenKey] = useState(null);

  const fetchVariables = async () => {
    setLoading(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.DOCHUB_VARIABLES,
      method: 'POST'
    });

    setLoading(false);

    if (error) {
      toast.error(error);
      return;
    }

    setModels(response?.models || []);
    setActiveTab(response?.models?.[0]?.label || '');
    setOpenKey(null);
  };

  useEffect(() => {
    fetchVariables();
  }, []);

  const activeModel = useMemo(() => models.find(m => m.label === activeTab), [models, activeTab]);

  const filteredGroups = useMemo(() => {
    const trimmedQuery = query.trim();

    return (activeModel?.groups || [])
      .map(group => ({
        ...group,
        variables: trimmedQuery ? group.variables.filter(v => matchesQuery(v, trimmedQuery)) : group.variables
      }))
      .filter(group => group.variables.length);
  }, [activeModel, query]);

  const expandedKey =
    openKey === '' ? null : openKey && filteredGroups.some(g => g.key === openKey) ? openKey : filteredGroups[0]?.key;

  const onVariableClick = reference => {
    copyToClipboard(reference);
    toast.success('Copied to clipboard');
  };

  return (
    <Container fluid className="h-100 py-3 px-md-3">
      <Card>
        <Card.Body>
          <h6 className="mb-0">DocHub Variables</h6>
          <p className="mb-0 smallFont text-muted">
            All references that can be used while printing in DocHub. Click on a reference to copy it.
          </p>
          <hr className="my-3" />
          {loading ? (
            <Loader />
          ) : (
            <>
              <Tabs
                tabs={models.map(m => m.label)}
                activeTab={activeTab}
                onTabSelect={tab => {
                  setActiveTab(tab);
                  setOpenKey(null);
                }}
                searchable={false}
              />
              <FormControl
                className="w-50"
                size="sm"
                placeholder="Search variables..."
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              {filteredGroups.length === 0 ? (
                <p className="smallFont text-muted my-3">No variables found.</p>
              ) : (
                filteredGroups.map(group => {
                  const isOpen = group.key === expandedKey;
                  return (
                    <div key={group.key} className="mt-3">
                      <h6
                        className="midFont mb-2 pointer d-inline-flex align-items-center"
                        onClick={() => setOpenKey(isOpen ? '' : group.key)}
                      >
                        {isOpen ? <ChevronDown className="mr-1" /> : <ChevronRight className="mr-1" />}
                        {group.label}
                        <Badge variant="light" className="ml-1">
                          {group.variables.length}
                        </Badge>
                      </h6>
                      {isOpen && (
                        <Row className="mx-0">
                          {group.variables.map(variable => (
                            <Col key={variable.reference} xs={12} md={6} lg={4} className="px-1 mb-2">
                              <div
                                className="d-flex justify-content-between align-items-center border rounded px-3 py-2 pointer h-100"
                                onClick={() => onVariableClick(variable.reference)}
                              >
                                <div className="mr-2" style={{ minWidth: 0 }}>
                                  <h6 className="midFont mb-0 text-break">{variable.label}</h6>
                                  <span className="smallFont text-muted text-break">{variable.reference}</span>
                                </div>
                                <Clipboard className="flex-shrink-0" />
                              </div>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  );
                })
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default DocHubVariables;
